import { SortingDirection } from "enums";
import {
  type FilterDefinition,
  type TableQueryHandlers,
  type TableQueryParamsResult,
  type TableQueryState,
  useTableQueryParams,
} from "hooks/useTableQueryParams";

import { Columns, ColumnToParamMap } from "../columnOptions";
import { FlowFiltersEnum } from "features/Library/Flows/types/models";

const DEFAULT_PAGE_SIZE = 10;

// Range filter param names
const DATE_CREATED_FROM_PARAM = `${FlowFiltersEnum.DateCreated}From`;
const DATE_CREATED_TO_PARAM = `${FlowFiltersEnum.DateCreated}To`;
const DATE_MODIFIED_FROM_PARAM = `${FlowFiltersEnum.DateModified}From`;
const DATE_MODIFIED_TO_PARAM = `${FlowFiltersEnum.DateModified}To`;

// Parser for numeric array values (IDs)
const parseNumberArray = (value: string): number | undefined => {
  if (!value) return undefined;
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : undefined;
};

// Maps API param -> display column name (reverse of ColumnToParamMap)
const paramToColumn = Object.entries(ColumnToParamMap).reduce<Record<string, string>>((acc, [column, param]) => {
  acc[param] = column.charAt(0).toUpperCase() + column.slice(1);
  return acc;
}, {});

// Define filter types based on FlowFiltersEnum
const filterDefinitions: ReadonlyArray<FilterDefinition<FlowFiltersEnum>> = [
  // Array filters (multi-select)
  { key: FlowFiltersEnum.Labels, type: "array" },
  { key: FlowFiltersEnum.SoftwareApplications, type: "array" },
  { key: FlowFiltersEnum.Packs, type: "array", parse: parseNumberArray },
  { key: FlowFiltersEnum.Publishers, type: "array", parse: parseNumberArray },
  { key: FlowFiltersEnum.GoalObjectives, type: "array", parse: parseNumberArray },
  { key: FlowFiltersEnum.GoalTypes, type: "array", parse: parseNumberArray },

  // Value filters (single select)
  { key: FlowFiltersEnum.Visibility, type: "value" },

  // Range filters (date ranges)
  {
    key: FlowFiltersEnum.DateCreated,
    type: "range",
    fromParam: DATE_CREATED_FROM_PARAM,
    toParam: DATE_CREATED_TO_PARAM,
  },
  {
    key: FlowFiltersEnum.DateModified,
    type: "range",
    fromParam: DATE_MODIFIED_FROM_PARAM,
    toParam: DATE_MODIFIED_TO_PARAM,
  },
];

const sortingKeys = Object.values(ColumnToParamMap);
const defaultSortBy = ColumnToParamMap[Columns.Modified.toLowerCase() as keyof typeof ColumnToParamMap];
const defaultSortDirection = SortingDirection.Descending;

const queryConfig = {
  defaultPageSize: DEFAULT_PAGE_SIZE,
  filters: filterDefinitions,
  sorting: {
    valid: sortingKeys,
    defaultSortBy,
    defaultDirection: defaultSortDirection,
    sortKeyToColumnName: paramToColumn,
    fallbackColumnName: Columns.Modified,
  },
} as const;

export type FlowsQueryState = TableQueryState<FlowFiltersEnum>;
export type FlowsQueryHandlers = TableQueryHandlers<FlowFiltersEnum>;
export type FlowsQueryParamsResult = TableQueryParamsResult<FlowFiltersEnum>;

export const useFlowsQueryParams = (): FlowsQueryParamsResult => {
  return useTableQueryParams<FlowFiltersEnum>(queryConfig);
};

export default useFlowsQueryParams;
